// GAVION X3: data quality client contract. Supabase: RPCs of 32_gavion_data_quality.sql (supabaseDataQualityClient);
// FakeRest demo and tests: localDataQualityClient (same codes, same shapes).
import type {
  DqCompany,
  DuplicateReason,
  FixKind,
  MergePicks,
} from "../../domain/dataQuality";
import type { SuspectEmail } from "../../domain/suspectEmails";

export type { SuspectEmail };

export type RelatedCounts = {
  contacts: number;
  deals: number;
  calls: number;
  notes: number;
  tasks: number;
  emails: number;
};

/** One side of a duplicate comparison (private.dq_company_card). */
export type CompanyCard = DqCompany & {
  counts: RelatedCounts;
  /** The email / phone is on the suppression list (merge carries it to the merged lead). */
  email_suppressed?: boolean;
  phone_suppressed?: boolean;
};

export type DuplicatePair = {
  a: CompanyCard;
  b: CompanyCard;
  reasons: DuplicateReason[];
};

export type DqSummary = Record<"duplicates" | FixKind, number>;

export type MergeResult = {
  survivorId: number;
  loserId: number;
  moved: Record<string, number>;
};

export interface DataQualityClient {
  summary(): Promise<DqSummary>;
  /** GAVION import duplicates (88_): the pairs waiting in « Doublons », for the menu count. */
  duplicateCount(): Promise<number>;
  duplicates(
    limit?: number,
  ): Promise<{ total: number; pairs: DuplicatePair[] }>;
  merge(
    survivorId: number,
    loserId: number,
    picks: MergePicks,
  ): Promise<MergeResult>;
  dismiss(a: number, b: number): Promise<void>;
  queue(
    kind: FixKind,
    limit?: number,
    offset?: number,
  ): Promise<{ total: number; rows: DqCompany[] }>;
  setPhone(
    id: number,
    phone: string,
  ): Promise<{ phoneE164: string; doNotCall: boolean }>;
  markBadNumber(id: number): Promise<void>;
  /** GAVION suspect emails (89_): the « Qualité des données » menu count (duplicate pairs + suspect emails). */
  attentionCount(): Promise<number>;
  /** GAVION suspect emails (89_): « Courriels douteux », by name. */
  suspectEmails(
    limit?: number,
  ): Promise<{ total: number; rows: SuspectEmail[] }>;
  /** Replace a compagnie's email (invalid_email, not_found). */
  setEmail(id: number, email: string): Promise<{ email: string }>;
  /** « Ignorer »: the compagnie's current email is not suspect. */
  dismissSuspectEmail(id: number): Promise<void>;
}

/** Stable P0001 code (forbidden, invalid_phone, phone_taken, lead_locked, ...); `detail` = e.g. the other lead id. */
export class DqApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly detail?: string,
  ) {
    super(code);
    this.name = "DqApiError";
  }
}
